import React, { useState } from 'react';

export function PhotoUpload() {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState('');
  const [validationResult, setValidationResult] = useState(null);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    setFile(selectedFile);
    setMessage('');
    setValidationResult(null);
    
    if (selectedFile) {
      // Basic client-side validation
      const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
      const maxSize = 10 * 1024 * 1024; // 10MB
      
      if (!allowedTypes.includes(selectedFile.type)) {
        setMessage('Only JPEG, PNG, and WebP images are allowed');
        setFile(null);
        return;
      }
      
      if (selectedFile.size > maxSize) {
        setMessage('File size must be less than 10MB');
        setFile(null);
        return;
      }
      
      setMessage('File selected. Click upload to validate and submit.');
    }
  };

  const handleUpload = async () => {
    if (!file) {
      setMessage('Please select a file');
      return;
    }

    setUploading(true);
    setMessage('');

    const formData = new FormData();
    formData.append('photo', file);
    formData.append('session_id', 'session-' + Date.now());

        try {
          const res = await fetch('https://should-i-get-reparations-backend.vercel.app/api/photos/upload', {
            method: 'POST',
            body: formData
          });
      const data = await res.json();
      
      if (data.success) {
        setMessage(data.data.message);
        setValidationResult(data.data.validation);
        setFile(null);
        // Reset file input
        document.querySelector('input[type="file"]').value = '';
      } else {
        setMessage(`Upload failed: ${data.error?.message || 'Unknown error'}`);
        if (data.error?.details) {
          setValidationResult(data.error.details);
        }
      }
    } catch (err) {
      setMessage(`Upload failed: ${err.message}`);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="photo-upload-container">
      <h2>Upload Your Photo</h2>
      <p className="upload-description">
        Upload a clear photo of yourself. The system performs basic validation 
        to ensure proper image format and size. Please ensure your photo contains 
        a clear human face for voting purposes.
      </p>
      
      <div className="upload-form">
        <div className="file-input-container">
          <input 
            type="file" 
            accept="image/*" 
            onChange={handleFileChange}
            className="file-input"
            id="photo-upload"
          />
          <label htmlFor="photo-upload" className="file-input-label">
            {file ? file.name : 'Choose Photo'}
          </label>
        </div>
        
        <button 
          onClick={handleUpload} 
          disabled={uploading || !file}
          className="upload-button"
        >
          {uploading ? 'Validating & Uploading...' : 'Upload Photo'}
        </button>
      </div>

      {message && (
        <div className={`message ${message.includes('success') ? 'success' : 'error'}`}>
          {message}
        </div>
      )}

      {validationResult && (
        <div className="validation-result">
          <h4>Validation Results:</h4>
          <div className="validation-details">
            <p><strong>Faces Detected:</strong> {validationResult.facesDetected}</p>
            <p><strong>Confidence:</strong> {(validationResult.confidence * 100).toFixed(1)}%</p>
          </div>
        </div>
      )}

      <div className="upload-guidelines">
        <h4>Photo Guidelines:</h4>
        <ul>
          <li>✅ Clear, well-lit photos of human faces</li>
          <li>✅ Single person or small groups (1-3 people)</li>
          <li>✅ Face should be clearly visible</li>
          <li>✅ JPEG, PNG, or WebP format</li>
          <li>✅ File size under 10MB</li>
          <li>❌ No objects, pets, or vehicles</li>
          <li>❌ No blurry or dark photos</li>
          <li>❌ No inappropriate content</li>
        </ul>
      </div>
    </div>
  );
}
